import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import type { LocaleCode } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ShiftReconciliationBar,
    ShiftFilters,
    ShiftRollupDrawer,
    ShiftTable,
} from './components';
import type { ShiftsAccess } from './shiftsAccess';
import type {
    BranchFilterOption,
    ShiftFilterState,
    ShiftReconciliation,
    ShiftRowModel,
} from './shiftsMeta';

type ShiftsPageViewProps = {
    access: ShiftsAccess;
    canRead: boolean;
    locale: LocaleCode;
    isLoading: boolean;
    filters: ShiftFilterState;
    branches: BranchFilterOption[];
    rows: ShiftRowModel[];
    reconciliation: ShiftReconciliation;
    staleCount: number;
    pageInfo: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };
    selectedShiftId: string | null;
    onRangeChange: (from: string, to: string) => void;
    onBranchChange: (branchId: string) => void;
    onStatusChange: (status: string) => void;
    onPageChange: (page: number) => void;
    onSelect: (shiftId: string) => void;
    onCloseRollup: () => void;
};

/**
 * The shift register — render only.
 *
 * Filters, a register, a pager, and a rollup drawer over all four. The count
 * above the table is the **server's** `totalCount`, not the rendered row count:
 * it answers "how many shifts match this filter", which is a different question
 * from "how many are on this page".
 */
export const ShiftsPageView: React.FC<ShiftsPageViewProps> = ({
    access,
    canRead,
    locale,
    isLoading,
    filters,
    branches,
    rows,
    reconciliation,
    staleCount,
    pageInfo,
    selectedShiftId,
    onRangeChange,
    onBranchChange,
    onStatusChange,
    onPageChange,
    onSelect,
    onCloseRollup,
}) => {
    const { t } = useTranslation('pages');

    if (!canRead) {
        return (
            <section
                aria-label={t('branches.shifts.title')}
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('branches-shifts-section')}
            >
                <p
                    className="rounded-xl bg-warning/10 px-4 py-3 text-sm text-warning"
                    {...getNodeTestIdProp('branches-shifts-access-notice')}
                >
                    {access.kind === 'kiosk'
                        ? t('branches.shifts.access.kiosk')
                        : t('branches.shifts.access.forbidden')}
                </p>
            </section>
        );
    }

    return (
        <section
            aria-label={t('branches.shifts.title')}
            className="flex flex-col gap-5"
            {...getNodeTestIdProp('branches-shifts-section')}
        >
            <header className="flex flex-col gap-1">
                <h1 className="text-xl font-semibold text-foreground">
                    {t('branches.shifts.title')}
                </h1>
                <p className="text-sm text-muted">
                    {t('branches.shifts.subtitle')}
                </p>
            </header>

            <ShiftFilters
                branches={branches}
                filters={filters}
                isDisabled={isLoading}
                onBranchChange={onBranchChange}
                onRangeChange={onRangeChange}
                onStatusChange={onStatusChange}
            />

            <span
                className="text-sm text-muted"
                {...getNodeTestIdProp('branches-shifts-count')}
            >
                {isLoading
                    ? t('branches.shifts.countPending')
                    : t('branches.shifts.count', {
                          count: pageInfo.totalCount,
                      })}
                {/* BOS-167: a shift open for eleven days had nothing on any
                    screen pointing at it. Counted here, chipped per row. */}
                {staleCount > 0 ? (
                    <span
                        className="ms-2 text-warning"
                        {...getNodeTestIdProp('branches-shifts-stale-count')}
                    >
                        {t('branches.shifts.staleCount', { count: staleCount })}
                    </span>
                ) : null}
            </span>

            <ShiftReconciliationBar reconciliation={reconciliation} />

            <ShiftTable isLoading={isLoading} onSelect={onSelect} rows={rows} />

            {pageInfo.totalPages > 1 ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3"
                    {...getNodeTestIdProp('branches-shifts-pager')}
                >
                    <span className="text-sm text-muted">
                        {t('branches.shifts.pageOf', {
                            page: pageInfo.page,
                            pages: pageInfo.totalPages,
                        })}
                    </span>
                    <span className="flex items-center gap-2">
                        <Button
                            isDisabled={isLoading || pageInfo.page <= 1}
                            onPress={() => onPageChange(pageInfo.page - 1)}
                            size="sm"
                            variant="outline"
                            {...getNodeTestIdProp('branches-shifts-prev-page')}
                        >
                            <Icon aria-hidden icon="gravity-ui:chevron-left" />
                            {t('branches.shifts.previous')}
                        </Button>
                        <Button
                            isDisabled={
                                isLoading ||
                                pageInfo.page >= pageInfo.totalPages
                            }
                            onPress={() => onPageChange(pageInfo.page + 1)}
                            size="sm"
                            variant="outline"
                            {...getNodeTestIdProp('branches-shifts-next-page')}
                        >
                            {t('branches.shifts.next')}
                            <Icon aria-hidden icon="gravity-ui:chevron-right" />
                        </Button>
                    </span>
                </div>
            ) : null}

            <ShiftRollupDrawer
                locale={locale}
                onClose={onCloseRollup}
                shiftId={selectedShiftId}
            />
        </section>
    );
};
